import React, { useContext, useEffect, useState } from 'react'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid'
import { useParams } from 'react-router-dom'

import { DEFAULT_LIMIT } from '../users/UsersViewModel'
import { ERole } from '../../../domain/interfaces/IRole'
import { ErrorToast, SuccessToast } from '../../../core/utils/toast-notifications/ToastNotifications'
import { getSubscriptions, deleteSubscription } from '../../../domain/usecases/subcriptions/SubscriptionUsecases'
import { ISubscription } from '../../../domain/interfaces/ISubscription'
import { showConfirmationDeleteMessage } from '../../components/dialogs/confirmation-dialog/ConfirmationDialog'
import { useOrganizationProvider } from '../../../providers/OrganizationProvider'
import usePermission from '../../../core/hooks/usePermission'
import useQuery from '../../../core/hooks/useQuery'

export interface SubscriptionPageContextType {
  isLoading: boolean
  error: string | undefined
  subscriptions: ISubscription[]
  showDeleteConfirmation: (id: string) => void
  loadSubscriptions: () => void
  userId?: string | undefined
  sortClickedHandler: (sorter: GridSortModel) => void
  totalCount: number
  setSearch: (searchKey: string) => void
  loadMore: (paginationModel: GridPaginationModel) => void
  canViewSubscriptions: boolean
  canCreateSubscriptions: boolean
  canEditSubscriptions: boolean
  errorObj?: any | undefined
  allFormsWithSubscriptions: ITransformedSubscription[] | undefined
  allUsersFormsWithSubscriptions: ITransformedSubscription[] | undefined
}

interface IListingForm {
  id: string
  formId: string
  title: string
  category: string | null
  lastSubmission: string | null
  isFavorited: boolean
  viewHiddenPermission: boolean
  viewSubmissionPermission: number
  createSubmissionPermission: number
  copilotStatus: boolean
  aiModelEndpoint: string | null
  aiVisionModelEndpoint: string | null
  aiScoringModelEndpoint: string | null
}

interface Subscription {
  id: string
  formId: string
  userId: string
  subscriptionType: 'Instant' | 'Daily' | 'instant' | 'daily'
  isSmsEnabled?: boolean
  isEmailEnabled?: boolean
  isPushEnabled?: boolean
  isInAppEnabled?: boolean
  isSupervisorSubscription: boolean
  form: any
  user: any
  role: any
}
export interface ITransformedSubscription {
  formId: string
  formName: string
  userId: string
  subscriptionType: 'Instant' | 'Daily' | 'instant' | 'daily'
  isSmsEnabled: boolean
  isEmailEnabled: boolean
  isPushEnabled: boolean
  isInAppEnabled: boolean
  isMatched?: boolean
  subscriptionId?: string | null | undefined
}

export function transformFormsWithSubscriptions(
  forms: IListingForm[],
  subscriptions: Subscription[],
  userId: string,
): ITransformedSubscription[] {
  // Create a map for quick subscription lookup using formId
  const subscriptionMap = new Map<string, Subscription>()
  subscriptions.forEach(sub => {
    subscriptionMap.set(sub.formId, sub)
  })

  // Separate forms into matched and unmatched
  const matchedForms: ITransformedSubscription[] = []
  const unmatchedForms: ITransformedSubscription[] = []

  forms.forEach(form => {
    // Try to match using form.formId (the main identifier)
    const matchingSubscription = subscriptionMap.get(form.formId)

    const transformedObject: ITransformedSubscription = {
      formId: form.formId, // Use form.formId as the identifier
      formName: form.title, // Use form.formId as the identifier
      userId: userId,
      // subscriptionType: matchingSubscription?.subscriptionType || 'Instant',
      subscriptionType: matchingSubscription?.subscriptionType || 'Instant',
      isSmsEnabled: matchingSubscription?.isSmsEnabled || false,
      isEmailEnabled: matchingSubscription?.isEmailEnabled !== undefined ? matchingSubscription.isEmailEnabled : false,
      isPushEnabled: matchingSubscription?.isPushEnabled || false,
      isInAppEnabled: matchingSubscription?.isInAppEnabled || false,
      subscriptionId: matchingSubscription?.id || null,
    }

    if (matchingSubscription) {
      matchedForms.push({ ...transformedObject, isMatched: true })
    } else {
      unmatchedForms.push({ ...transformedObject, isMatched: false })
    }
  })

  // Return matched forms first, then unmatched
  return [...matchedForms, ...unmatchedForms]
}

const SubscriptionPageContext = React.createContext<SubscriptionPageContextType | null>(null)
SubscriptionPageContext.displayName = 'SubscriptionPageContext'

interface SubscriptionPageVMProps {
  children: React.ReactNode
}

export const SubscriptionPageViewModel: React.FC<SubscriptionPageVMProps> = ({ children }) => {
  const { userId } = useParams()
  const { navForms } = useOrganizationProvider() // Assuming you have subscription links

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [errorObj, setErrorObj] = useState<any | undefined>(undefined)
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  // NEED TO CHECK WHEN FEATURE ADD TO ROLES
  const canViewSubscriptions = usePermission(ERole.UserEdit)
  const canCreateSubscriptions = usePermission(ERole.UserEdit)
  const canEditSubscriptions = usePermission(ERole.UserEdit)
  const [allFormsWithSubscriptions, setAllFormsWithSubscriptions] = useState<ITransformedSubscription[] | undefined>(
    undefined,
  )
  const [allUsersFormsWithSubscriptions, setAllUsersFormsWithSubscriptions] = useState<
    ITransformedSubscription[] | undefined
  >(undefined)

  const queryHelper = useQuery({
    ...(userId ? { userId: userId } : {}),
    skip: 0,
    limit: DEFAULT_LIMIT,
  })

  const loadMore = (paginationModel: GridPaginationModel) => {
    const fetchSkip = paginationModel.page * paginationModel.pageSize
    getOrgSubscriptions(fetchSkip, query)
  }

  const { query, getQueryStringFromQuery, setNew, defaultQuery } = queryHelper

  const getOrgSubscriptions = async (fetchSkip: number, query: Record<string, any>) => {
    setIsLoading(true)
    setError(undefined)

    const newQuery = { ...query, skip: fetchSkip }
    const res = await getSubscriptions(getQueryStringFromQuery(newQuery))
    setIsLoading(false)
    if (res.isLeft()) {
      setError(res.left?.message ?? 'Error loading subscriptions.')
      setErrorObj(res.left)
      return
    }
    setSubscriptions(res.right?.data ?? [])
    setTotalCount(res.right?.total_count || 0)
    setNew(newQuery)
  }

  const showDeleteConfirmation = async (id: string) => {
    const confirmMessage = '1 subscription'

    showConfirmationDeleteMessage(confirmMessage, async () => {
      const res = await deleteSubscription(id)
      if (res.isRight()) {
        getOrgSubscriptions(0, defaultQuery)
        SuccessToast({ title: `Successfully deleted ${confirmMessage}` })
        return
      }
      ErrorToast({ title: 'Failed to delete subscription' })
    })
  }

  const sortClickedHandler = (sorter: GridSortModel) => {
    const { field, sort } = sorter[0]
    const newQuery = {
      ...query,
      skip: 0,
      sortBy: field,
      sortOrder: sort?.toUpperCase(),
    }
    getOrgSubscriptions(0, newQuery)
  }

  const setSearch = (searchKey: string) => {
    const newQuery = {
      ...query,
      skip: 0,
      searchKey,
    }
    getOrgSubscriptions(0, newQuery)
  }

  const loadSubscriptions = async () => {
    setError(undefined)
    setIsLoading(true)
    setSubscriptions([])
    getOrgSubscriptions(0, query)
  }

  useEffect(() => {
    loadSubscriptions()
  }, [])

  useEffect(() => {
    if (!navForms.length || isLoading) return
    const f = navForms as any
    const s = subscriptions as any
    const allFormsWithSubscriptions = userId ? transformFormsWithSubscriptions(f, s, userId) : []
    const allUserFormsWithSubscriptions = allFormsWithSubscriptions.filter(d => d.isMatched)
    setAllFormsWithSubscriptions(allFormsWithSubscriptions)
    setAllUsersFormsWithSubscriptions(allUserFormsWithSubscriptions)
  }, [navForms, isLoading])

  return (
    <SubscriptionPageContext.Provider
      value={{
        error,
        isLoading,
        subscriptions,
        showDeleteConfirmation,
        loadSubscriptions,
        userId,
        sortClickedHandler,
        totalCount,
        setSearch,
        loadMore,
        canViewSubscriptions,
        canCreateSubscriptions,
        canEditSubscriptions,
        errorObj,
        allFormsWithSubscriptions,
        allUsersFormsWithSubscriptions,
      }}
    >
      {children}
    </SubscriptionPageContext.Provider>
  )
}

export function useSubscriptionPageViewModel() {
  const context = useContext(SubscriptionPageContext)
  if (!context) {
    throw new Error('useSubscriptionPageViewModel must be used within a SubscriptionPageViewModel')
  }
  return context
}
